// Copyright (C) 2012-present, The Authors. This program is free software: you can redistribute it and/or  modify it under the terms of the GNU Affero General Public License, version 3, as published by the Free Software Foundation. This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details. You should have received a copy of the GNU Affero General Public License along with this program.  If not, see <http://www.gnu.org/licenses/>.

import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Box, Heading, Text, Input, Textarea, Button, Label, Flex } from 'theme-ui'

import Spinner from '../framework/Spinner'
import strings from '../../strings/strings'
import { useUser } from '../../util/auth'
import { populateUserStore } from '../../actions'
import PolisNet from '../../util/net'

const fieldSx = { mb: [3] }
const sectionSx = {
  mb: [4],
  p: [3],
  border: '1px solid',
  borderColor: 'lightGray',
  borderRadius: '8px'
}

const Account = () => {
  const userContext = useUser()
  const dispatch = useDispatch()
  const user = userContext?.user

  // Profile fields (on the user record)
  const [hname, setHname] = useState('')
  const [position, setPosition] = useState('')
  const [phone, setPhone] = useState('')

  // Organization fields
  const [orgName, setOrgName] = useState('')
  const [orgDescription, setOrgDescription] = useState('')
  const [orgLogoUrl, setOrgLogoUrl] = useState('')
  const [orgContactEmail, setOrgContactEmail] = useState('')
  const [orgContactPhone, setOrgContactPhone] = useState('')

  const [savingProfile, setSavingProfile] = useState(false)
  const [savingOrg, setSavingOrg] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [status, setStatus] = useState('')

  // Organization members + invites
  const [members, setMembers] = useState([])
  const [invites, setInvites] = useState([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)

  useEffect(() => {
    if (!user) return
    setHname(user.hname || '')
    setPosition(user.position || '')
    setPhone(user.phone || '')
    const org = user.org || {}
    setOrgName(org.name || '')
    setOrgDescription(org.description || '')
    setOrgLogoUrl(org.logo_url || '')
    setOrgContactEmail(org.contact_email || '')
    setOrgContactPhone(org.contact_phone || '')
  }, [user])

  const refresh = () => dispatch(populateUserStore())

  const loadOrgLists = async () => {
    try {
      const m = await PolisNet.polisGet('/api/v3/organizations/members')
      setMembers(Array.isArray(m) ? m : [])
    } catch (e) {
      /* ignore */
    }
    try {
      const inv = await PolisNet.polisGet('/api/v3/organizations/invites')
      setInvites(Array.isArray(inv) ? inv : [])
    } catch (e) {
      /* ignore */
    }
  }

  useEffect(() => {
    if (user && user.oid) loadOrgLists()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const handleInvite = async () => {
    if (!inviteEmail || inviteEmail.indexOf('@') === -1) {
      setStatus(strings('pg_account_invite_bad_email'))
      return
    }
    setInviting(true)
    setStatus('')
    try {
      await PolisNet.polisPost('/api/v3/organizations/invites', {
        email: inviteEmail
      })
      setInviteEmail('')
      setStatus(strings('pg_account_invite_sent'))
      loadOrgLists()
    } catch (e) {
      setStatus(strings('pg_account_invite_error'))
    } finally {
      setInviting(false)
    }
  }

  const saveProfile = async () => {
    setSavingProfile(true)
    setStatus('')
    try {
      await PolisNet.polisPut('/api/v3/users', { hname, position, phone })
      refresh()
      setStatus(strings('pg_account_saved'))
    } catch (e) {
      setStatus(strings('pg_account_save_error'))
    } finally {
      setSavingProfile(false)
    }
  }

  const saveOrg = async () => {
    setSavingOrg(true)
    setStatus('')
    try {
      await PolisNet.polisPut('/api/v3/organizations', {
        name: orgName,
        description: orgDescription,
        logo_url: orgLogoUrl,
        contact_email: orgContactEmail,
        contact_phone: orgContactPhone
      })
      refresh()
      setStatus(strings('pg_account_saved'))
    } catch (e) {
      setStatus(strings('pg_account_save_error'))
    } finally {
      setSavingOrg(false)
    }
  }

  const handleLogoFile = (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    if (file.size > 500 * 1024) {
      setStatus(strings('pg_account_logo_too_large'))
      e.target.value = ''
      return
    }
    const reader = new FileReader()
    reader.onload = async () => {
      setUploadingLogo(true)
      setStatus('')
      try {
        const result = await PolisNet.polisPost('/api/v3/organizations/logo', {
          logo: reader.result
        })
        if (result && result.logo_url) {
          setOrgLogoUrl(result.logo_url)
        }
        refresh()
        setStatus(strings('pg_account_saved'))
      } catch (err) {
        setStatus(strings('pg_account_logo_error'))
      } finally {
        setUploadingLogo(false)
        e.target.value = ''
      }
    }
    reader.readAsDataURL(file)
  }

  const buildAccountMarkup = () => {
    return (
      <Box sx={{ maxWidth: '640px' }}>
        <Heading
          as="h3"
          sx={{ fontSize: [3, null, 4], lineHeight: 'body', mb: [3, null, 4] }}>
          {strings('pg_account')}
        </Heading>
        <p>
          {strings('pg_hi')} {user?.hname?.split(' ')[0]}!
        </p>

        <Box sx={sectionSx}>
          <Heading as="h4" sx={{ fontSize: [2, null, 3], mb: [3] }}>
            {strings('pg_account_profile')}
          </Heading>

          <Box sx={fieldSx}>
            <Label htmlFor="acct-name">{strings('pg_account_name')}</Label>
            <Input
              id="acct-name"
              value={hname}
              onChange={(e) => setHname(e.target.value)}
            />
          </Box>

          <Box sx={fieldSx}>
            <Label>{strings('pg_account_email')}</Label>
            <Text sx={{ color: 'mediumGray' }}>{user?.email}</Text>
          </Box>

          <Box sx={fieldSx}>
            <Label htmlFor="acct-position">{strings('pg_account_position')}</Label>
            <Input
              id="acct-position"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
          </Box>

          <Box sx={fieldSx}>
            <Label htmlFor="acct-phone">{strings('pg_account_phone')}</Label>
            <Input
              id="acct-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Box>

          <Button onClick={saveProfile} disabled={savingProfile}>
            {savingProfile ? strings('pg_account_saving') : strings('pg_account_save')}
          </Button>
        </Box>

        <Box sx={sectionSx}>
          <Heading as="h4" sx={{ fontSize: [2, null, 3], mb: [3] }}>
            {strings('pg_account_org')}
          </Heading>

          <Box sx={fieldSx}>
            <Label htmlFor="org-name">{strings('pg_account_org_name')}</Label>
            <Input
              id="org-name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />
          </Box>

          <Box sx={fieldSx}>
            <Label htmlFor="org-description">
              {strings('pg_account_org_description')}
            </Label>
            <Textarea
              id="org-description"
              rows={4}
              value={orgDescription}
              onChange={(e) => setOrgDescription(e.target.value)}
            />
          </Box>

          <Box sx={fieldSx}>
            <Label>{strings('pg_account_org_logo')}</Label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleLogoFile}
              disabled={uploadingLogo}
            />
            {uploadingLogo ? (
              <Text sx={{ fontSize: [1], color: 'mediumGray', mt: [1] }}>
                {strings('pg_account_logo_uploading')}
              </Text>
            ) : null}
            {orgLogoUrl ? (
              <Box sx={{ mt: [2] }}>
                <img
                  src={orgLogoUrl}
                  alt="logo"
                  style={{ maxHeight: '64px', maxWidth: '200px' }}
                />
              </Box>
            ) : null}
            <Box sx={{ mt: [2] }}>
              <Label htmlFor="org-logo-url" sx={{ fontSize: [1] }}>
                {strings('pg_account_org_logo_url')}
              </Label>
              <Input
                id="org-logo-url"
                value={orgLogoUrl}
                onChange={(e) => setOrgLogoUrl(e.target.value)}
              />
            </Box>
          </Box>

          <Box sx={fieldSx}>
            <Label htmlFor="org-cemail">
              {strings('pg_account_org_contact_email')}
            </Label>
            <Input
              id="org-cemail"
              value={orgContactEmail}
              onChange={(e) => setOrgContactEmail(e.target.value)}
            />
          </Box>

          <Box sx={fieldSx}>
            <Label htmlFor="org-cphone">
              {strings('pg_account_org_contact_phone')}
            </Label>
            <Input
              id="org-cphone"
              value={orgContactPhone}
              onChange={(e) => setOrgContactPhone(e.target.value)}
            />
          </Box>

          <Button onClick={saveOrg} disabled={savingOrg}>
            {savingOrg ? strings('pg_account_saving') : strings('pg_account_save')}
          </Button>
        </Box>

        <Box sx={sectionSx}>
          <Heading as="h4" sx={{ fontSize: [2, null, 3], mb: [3] }}>
            {strings('pg_account_members')}
          </Heading>

          <Box sx={fieldSx}>
            {members.length ? (
              members.map((m) => (
                <Text key={m.uid} sx={{ display: 'block', mb: [1] }}>
                  {m.hname || m.email}
                  {m.position ? ` — ${m.position}` : ''}{' '}
                  <Text as="span" sx={{ color: 'mediumGray' }}>
                    {m.email}
                  </Text>
                </Text>
              ))
            ) : (
              <Text sx={{ color: 'mediumGray' }}>
                {strings('pg_account_members_empty')}
              </Text>
            )}
          </Box>

          <Box sx={fieldSx}>
            <Label htmlFor="invite-email">
              {strings('pg_account_invite_email')}
            </Label>
            <Flex sx={{ gap: [2] }}>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                placeholder="name@example.com"
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <Button onClick={handleInvite} disabled={inviting}>
                {inviting
                  ? strings('pg_account_inviting')
                  : strings('pg_account_invite')}
              </Button>
            </Flex>
          </Box>

          {invites.length ? (
            <Box sx={fieldSx}>
              <Text sx={{ fontWeight: 'bold', mb: [1] }}>
                {strings('pg_account_pending_invites')}
              </Text>
              {invites.map((inv) => (
                <Text
                  key={inv.invite_id}
                  sx={{ display: 'block', color: 'mediumGray' }}>
                  {inv.email}
                </Text>
              ))}
            </Box>
          ) : null}
        </Box>

        {status ? (
          <Text sx={{ color: 'primary', mt: [2] }}>{status}</Text>
        ) : null}
      </Box>
    )
  }

  return <div>{user?.hname ? buildAccountMarkup() : <Spinner />}</div>
}

export default Account
